/**
 * PackYearsInput — question type 'pack-years' (Lungs, Dr. Rami 2026-10-05: two inputs, app-calculated, ONE
 * question id). Saved as one English answer the doctor and the summary read, e.g.
 * "1 pack/day × 20 years = 20 pack-years", or the question's own "Never smoked" option.
 */
import { useI18n } from '../../i18n'

const MIN = { packs: 0.5, years: 1 }
const MAX = { packs: 6, years: 80 }
const STEP = { packs: 0.5, years: 1 }

export function formatPackYears(packs, years) {
  const py = Math.round(packs * years * 10) / 10
  return `${packs} pack${packs === 1 ? '' : 's'}/day × ${years} year${years === 1 ? '' : 's'} = ${py} pack-years`
}

export function parsePackYears(value) {
  const m = typeof value === 'string' && value.match(/^([\d.]+) packs?\/day × (\d+) years? =/)
  return m ? { packs: Number(m[1]), years: Number(m[2]) } : null
}

export default function PackYearsInput({ question, value, onChange, disabled, tq, to }) {
  const { t } = useI18n()
  const never = question.never && value === question.never
  const parsed = parsePackYears(value)
  const packs = parsed?.packs ?? 1
  const years = parsed?.years ?? 10
  const set = (field, delta) => {
    if (disabled) return
    const next = { packs, years }
    next[field] = Math.min(MAX[field], Math.max(MIN[field], Math.round((next[field] + delta) * 10) / 10))
    onChange(formatPackYears(next.packs, next.years))
  }
  const stepper = (field, label) => (
    <div>
      <p className="text-[13px] text-gray-700 mb-1.5">{tq(label)}</p>
      <div className={`flex items-center h-[42px] rounded-full border border-gray-200 overflow-hidden ${never ? 'opacity-40' : ''}`}>
        <button type="button" aria-label={t('describe.packYearsLess')} disabled={disabled || never} onClick={() => set(field, -STEP[field])} className="w-[46px] h-full bg-gray-50 text-lg font-bold text-[#15354F]">−</button>
        <output className="flex-1 text-center font-bold tabular-nums">{field === 'packs' ? packs : years}</output>
        <button type="button" aria-label={t('describe.packYearsMore')} disabled={disabled || never} onClick={() => set(field, STEP[field])} className="w-[46px] h-full bg-gray-50 text-lg font-bold text-[#15354F]">+</button>
      </div>
    </div>
  )
  const total = never ? 0 : Math.round(packs * years * 10) / 10
  return (
    <div className="space-y-3">
      {question.helper && <p className="text-xs text-gray-500 -mt-1">{tq(question.helper)}</p>}
      {stepper('packs', question.inputs?.[0])}
      {stepper('years', question.inputs?.[1])}
      <div className="flex items-baseline justify-between rounded-xl bg-[#E9F8FF] px-3 py-2.5 text-[12.5px] text-gray-600">
        <span className="tabular-nums">{never ? to(question.never) : `${packs} × ${years}`}</span>
        <span>
          <strong className="text-xl text-[#15354F] tabular-nums">{parsed || never ? total : '—'}</strong>{' '}
          <span className="font-semibold">{t('describe.packYearsUnit')}</span>
        </span>
      </div>
      {question.never && (
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange(never ? '' : question.never)}
          className={`w-full p-3 rounded-xl border text-left flex items-center gap-3 transition-all ${never ? 'border-orella-500 bg-orella-50' : 'border-gray-200'}`}
        >
          <span className={`w-5 h-5 rounded-full border-2 flex-shrink-0 ${never ? 'border-orella-500 shadow-[inset_0_0_0_4px_#46A8F9]' : 'border-gray-300'}`} />
          <span className="text-sm text-gray-700">{to(question.never)}</span>
        </button>
      )}
    </div>
  )
}
